import { apiClient as api } from "./httpClient";
import type { ProposalCardData } from "./assistantsService";

// Chat com os agentes de IA (M5/M6). Endpoints autenticados (não exigem a permissão de admin da
// tela de cadastro). O acesso é governado no servidor: vínculo com a entidade + atribuição (M4) +
// IA habilitada/aceita (M0).

export interface MyAgent {
  id: string;
  displayName: string;
  hasAvatar: boolean;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  updatedAt: string;
}

export interface ChatMessage {
  role: string; // "user" | "assistant"
  content: string;
  createdAt: string;
  // Área dos Assistentes: quem falou (duas vozes na conversa) e os cartões de proposta da fala
  agentId?: string | null;
  agentName?: string | null;
  proposals?: ProposalCardData[] | null;
}

export interface SendResult {
  conversationId: string;
  reply: string;
  // Resposta cortada por atingir o teto de saída do modelo (stop_reason=max_tokens) — a UI avisa.
  truncated: boolean;
  // Área dos Assistentes: cartões que o agente de alteração (subagente) preparou — ficam embaixo desta resposta
  proposals?: ProposalCardData[] | null;
  // Reserva: o agente de consulta não repassou e o de alteração respondeu com a própria voz
  handoff?: { agentId: string; agentName?: string | null; content: string; proposals: ProposalCardData[] } | null;
}

/** Rastro (D-OP-38): por resposta do agente, as ferramentas chamadas. Só os nomes dos campos, nunca os valores. */
export interface TraceCall {
  tool: string;
  agentName?: string | null;
  argumentKeys: string;
  outcome: string;
  durationMs: number;
  resultChars: number;
  createdAt: string;
}

export interface TraceTurn {
  messageId: string;
  createdAt: string;
  agentName?: string | null;
  excerpt: string;
  calls: TraceCall[];
}

export const aiChatService = {
  trace: async (conversationId: string): Promise<TraceTurn[]> =>
    (await api.get(`/ai-chat/conversations/${conversationId}/trace`)).data,
  myAgents: async (entityId: string): Promise<MyAgent[]> => {
    const response = await api.get(`/ai-chat/my-agents/${entityId}`);
    return response.data;
  },

  // RN-573: area vazia = chat comum; days 7, 15 ou 30 (padrão 7); search procura no título e nas mensagens
  conversations: async (
    agentId: string,
    filter: { area?: string; days?: number; search?: string } = {},
  ): Promise<ConversationSummary[]> => {
    const response = await api.get(`/ai-chat/conversations/${agentId}`, {
      params: { area: filter.area || undefined, days: filter.days, search: filter.search?.trim() || undefined },
    });
    return response.data;
  },

  messages: async (conversationId: string): Promise<ChatMessage[]> => {
    const response = await api.get(`/ai-chat/conversations/${conversationId}/messages`);
    return response.data;
  },

  send: async (
    agentId: string,
    conversationId: string | null,
    message: string,
    currentPage?: string | null,
    // Assistente do prontuário: prende a conversa NOVA ao paciente (depois o servidor usa o da conversa)
    patientId?: string | null,
    // Área dos Assistentes (RN-567): o agente é o de consulta da área e a passagem fica disponível
    areaKey?: string | null
  ): Promise<SendResult> => {
    const response = await api.post("/ai-chat/messages", { agentId, conversationId, message, currentPage, patientId, areaKey });
    return response.data;
  },

  // Avatar do agente para o chat (endpoint próprio, sem exigir permissão de admin). O <img> não manda
  // header de auth → busca como blob e devolve object URL (revogar ao desmontar).
  avatar: async (agentId: string): Promise<string> => {
    const response = await api.get(`/ai-chat/agents/${agentId}/avatar`, { responseType: "blob" });
    return URL.createObjectURL(response.data);
  },
};
